import { AutoEndWarning } from '~/common/api/zeiterfassungServerApi/AutoEndWarningApi';
import AutoEndWarningApi from '~/common/api/zeiterfassungServerApi/AutoEndWarningApi';
import { LogoutWarning } from '~/common/api/zeiterfassungServerApi/LogoutWarningApi';
import LogoutWarningApi from '~/common/api/zeiterfassungServerApi/LogoutWarningApi';
import { PauseWarning } from '~/common/api/zeiterfassungServerApi/PauseWarningApi';
import PauseWarningApi from '~/common/api/zeiterfassungServerApi/PauseWarningApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import useTranslate from '~/common/utils/translate';
import WarningDrawer from './WarningDrawer';
import { useMemo, useState } from 'react';

type WarningButtonsProps = {
  autoEndWarnings: AutoEndWarning[];
  logoutWarnings: LogoutWarning[];
  pauseWarnings: PauseWarning[];
  employee: Employee;
  date: number;
  isDetailsOpen: boolean;
};

export const WarningButtons: React.FC<WarningButtonsProps> = ({
  autoEndWarnings,
  logoutWarnings,
  pauseWarnings,
  employee,
  date,
  isDetailsOpen: isDetailsOpen,
}) => {
  const t = useTranslate();

  const {
    isOpen: isDrawerOpen,
    onOpen: setDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();
  const { mutateAsync: updateSeenStatusAutoEnd } =
    AutoEndWarningApi.hooks.useAdminHasSeenWarning();
  const { mutateAsync: updateSeenStatusLogoutWarning } =
    LogoutWarningApi.hooks.useAdminHasSeenWarning();

  const { mutateAsync: updateSeenStatusPauseWarning } =
    PauseWarningApi.hooks.useAdminHasSeenWarning();

  const allWarnings = useMemo(() => {
    return [...autoEndWarnings, ...logoutWarnings, ...pauseWarnings];
  }, [autoEndWarnings, logoutWarnings, pauseWarnings]);

  const hasWarnings = allWarnings.length > 0;

  const [selectedWarning, setSelectedWarning] = useState<
    AutoEndWarning | LogoutWarning | PauseWarning | null
  >(null);

  const selectWarningAndOpenDrawer = (
    warning: AutoEndWarning | LogoutWarning | PauseWarning,
    type: string,
  ) => {
    warning.type = type;
    setSelectedWarning(warning);
    setDrawerOpen();
  };
  if (!hasWarnings) return null;

  return (
    <>
      <Flex direction="column" ml={4} gap={2}>
        {autoEndWarnings.map((warning) => (
          <Flex key={warning.id} align="center" gap={2}>
            <Button
              size="xs"
              w="158px"
              backgroundColor={
                warning.seen_by_admin ? 'green.400' : 'yellow.400'
              }
              onClick={() => selectWarningAndOpenDrawer(warning, 'autoEnd')}
            >
              {t('warning.buttons.autoEnd.openDrawer')}
            </Button>
            {!warning.seen_by_admin && isDetailsOpen && (
              <Button
                size="xs"
                backgroundColor="green.600"
                onClick={() => updateSeenStatusAutoEnd(warning)}
              >
                {t('warning.buttons.autoEnd.setSeen')}
              </Button>
            )}
          </Flex>
        ))}

        {logoutWarnings.map((warning) => (
          <Flex key={warning.id} align="center" gap={2}>
            <Button
              size="xs"
              w="158px"
              backgroundColor={
                warning.seen_by_admin ? 'green.400' : 'yellow.400'
              }
              onClick={() => selectWarningAndOpenDrawer(warning, 'logout')}
            >
              {t('warning.buttons.PauseAndLogout.openDrawer')}
            </Button>
            {!warning.seen_by_admin && (
              <Button
                size="xs"
                backgroundColor="green.600"
                onClick={() => updateSeenStatusLogoutWarning(warning)}
              >
                {t('warning.buttons.PauseAndLogout.setSeen')}
              </Button>
            )}
          </Flex>
        ))}

        {pauseWarnings.map((warning) => (
          <Flex key={warning.id} align="center" gap={2}>
            <Button
              size="xs"
              w="158px"
              backgroundColor={
                warning.seen_by_admin ? 'green.400' : 'yellow.400'
              }
              onClick={() => selectWarningAndOpenDrawer(warning, 'pause')}
            >
              {t('warning.buttons.PauseAndLogout.openDrawer')}
            </Button>
            {!warning.seen_by_admin && (
              <Button
                size="xs"
                backgroundColor="green.600"
                onClick={() => updateSeenStatusPauseWarning(warning)}
              >
                {t('warning.buttons.PauseAndLogout.setSeen')}
              </Button>
            )}
          </Flex>
        ))}
      </Flex>
      <WarningDrawer
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        warning={selectedWarning!}
        employee={employee}
        date={date}
      />
    </>
  );
};

export default WarningButtons;
